import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { Trophy, Flame, ChevronRight, BookOpen } from "lucide-react";
import { getBandeiraUrl } from "../utils/bandeiras";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/15f31867-a42b-4fb8-8b3e-49ddef9b80ee/images/f0b327a9f57cd69c746617794269dd520db3b2263225f8d0912ee8e983314894.png";

function formatarBRL(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));
}

export default function PortalPublico() {
  const [ranking, setRanking] = useState([]);
  const [ultimos, setUltimos] = useState([]);
  const [proximos, setProximos] = useState([]);
  const [informativos, setInformativos] = useState([]);
  const [premiacao, setPremiacao] = useState(null);

  useEffect(() => {
    (async () => {
      const [rk, u, p, i, pr] = await Promise.all([
        api.get("/publico/ranking").catch(() => ({ data: { ranking: [] } })),
        api.get("/publico/ultimos-resultados", { params: { limit: 4 } }).catch(() => ({ data: [] })),
        api.get("/publico/proximos-jogos", { params: { limit: 4 } }).catch(() => ({ data: [] })),
        api.get("/publico/informativos").catch(() => ({ data: [] })),
        api.get("/publico/premiacao").catch(() => ({ data: null })),
      ]);
      setRanking(rk.data?.ranking || []);
      setUltimos(u.data || []);
      setProximos(p.data || []);
      setInformativos(i.data || []);
      setPremiacao(pr.data);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-superficie-base text-white" data-testid="portal-publico">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-superficie-base/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-publico">
            <img
              src="https://customer-assets.emergentagent.com/job_palpites-mundo/artifacts/lm7h8g1u_Microsoft_Word_-_SolicitaA_C2_A7A_C2_A3o_de_Cancelamento_-_Modelo_assinado.pdf.png"
              alt="Bolão da Copa"
              className="w-11 h-11 object-contain"
              style={{ mixBlendMode: "screen", filter: "drop-shadow(0 2px 6px rgba(255,215,0,0.35))" }}
            />
            <div>
              <div className="font-heading font-bold uppercase text-base leading-none">Bolão da Copa</div>
              <div className="text-[10px] tracking-[0.3em] text-amarelobrasil uppercase">FIFA 2026</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-3">
            <LinkAncora href="#premio">Premiação</LinkAncora>
            <LinkAncora href="#proximos">Próximos</LinkAncora>
            <LinkAncora href="#ranking">Ranking</LinkAncora>
            <LinkAncora href="#regras">Regulamento</LinkAncora>
          </nav>

          <Link
            to="/login"
            className="px-7 py-3 rounded-full text-sm font-heading font-bold uppercase tracking-widest text-white border-2 transition-all hover:bg-sky-400/20 hover:border-sky-300"
            style={{
              borderColor: "rgba(135, 206, 250, 0.7)",
              backgroundColor: "rgba(135, 206, 250, 0.12)",
              backdropFilter: "blur(4px)",
            }}
            data-testid="link-login"
          >
            Entrar
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url('${HERO_BG}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-superficie-base/40 via-superficie-base/80 to-superficie-base" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-amarelobrasil-soft text-amarelobrasil text-[10px] font-bold uppercase tracking-[0.3em]">
                <Flame className="w-3 h-3" /> Copa do Mundo 2026
              </div>
              <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.9]">
                Sua paixão,<br />
                <span className="text-verdebrasil">seu palpite</span>,<br />
                <span className="text-amarelobrasil">sua glória</span>.
              </h1>
              <p className="mt-6 text-base md:text-lg text-white/70 max-w-xl">
                 Bolão da Copa do Mundo FIFA 2026. Faça seus palpites até 2h antes de cada jogo, acumule pontos e dispute o prêmio em jogo.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/login"
                  className="px-8 py-4 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark hover:glow-green transition-all flex items-center gap-2"
                  data-testid="btn-hero-entrar"
                >
                  Entrar no Bolão <ChevronRight className="w-4 h-4" />
                </Link>
                <a
                  href="#ranking"
                  className="px-8 py-4 rounded-md font-heading uppercase tracking-widest text-sm border-2 border-amarelobrasil text-amarelobrasil hover:bg-amarelobrasil hover:text-superficie-base transition-all"
                  data-testid="btn-hero-ranking"
                >
                  Ver Ranking
                </a>
              </div>
            </div>

            {/* Taça com o prêmio total */}
            <TacaPremio total={premiacao?.totalArrecadado || 0} />
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16 space-y-20">
        {/* Próximos jogos */}
        <section id="proximos">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Em breve</div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-1">Próximos Jogos</h2>
            </div>
            <Link to="/login" className="text-xs font-bold uppercase tracking-widest text-white/60 hover:text-white">
              Palpitar →
            </Link>
          </div>
          {proximos.length === 0 ? (
            <div className="text-white/40 text-sm">Nenhum jogo agendado no momento.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {proximos.map((j) => (
                <MiniJogo key={j.id} jogo={j} futuro />
              ))}
            </div>
          )}
        </section>

        {/* Ranking pódio */}
        <section id="ranking">
          <div className="mb-8">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Classificação Geral</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-1">Pódio do Bolão</h2>
          </div>
          {ranking.length === 0 ? (
            <div className="surface-card rounded-2xl p-8 text-white/40 text-sm text-center">
              Ainda não há ranking — os primeiros resultados serão lançados em breve.
            </div>
          ) : (
            <>
              <Podio ranking={ranking.slice(0, 3)} />
              <div className="mt-8 surface-card rounded-2xl divide-y divide-white/5">
                {ranking.slice(0, 10).map((r, i) => (
                  <div key={r.usuarioId} className="flex items-center justify-between px-6 py-4" data-testid={`linha-ranking-${i + 1}`}>
                    <div className="flex items-center gap-4 min-w-0">
                      <span className="font-heading font-black text-2xl w-10 tabular-nums text-white/40">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0">
                        <div className="font-bold truncate">{r.usuario?.nomePublico || r.usuario?.nomeCompleto}</div>
                        <div className="text-[10px] uppercase tracking-widest text-white/40">
                          {r.totalPlacaresExatos} exatos • {r.totalPalpites} palpites
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-black text-xl text-amarelobrasil tabular-nums">{r.pontuacaoTotal}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/40">pontos</div>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/ranking-publico"
                className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amarelobrasil hover:text-white transition-colors"
                data-testid="link-ver-ranking-completo"
              >
                Ver ranking completo <ChevronRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </section>

        {/* Últimos resultados + Informativos */}
        <section className="grid md:grid-cols-2 gap-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Resultados</div>
            <h2 className="font-heading text-3xl font-bold uppercase mt-1 mb-6">Últimos Jogos</h2>
            {ultimos.length === 0 ? (
              <div className="text-white/40 text-sm">Aguardando primeiros resultados.</div>
            ) : (
              <div className="space-y-3">
                {ultimos.map((j) => (
                  <MiniJogo key={j.id} jogo={j} />
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Informativos</div>
            <h2 className="font-heading text-3xl font-bold uppercase mt-1 mb-6">Avisos da Organização</h2>
            {informativos.length === 0 ? (
              <div className="text-white/40 text-sm">Nenhum informativo publicado ainda.</div>
            ) : (
              <div className="space-y-3">
                {informativos.slice(0, 5).map((inf) => (
                  <div key={inf.id} className="surface-card rounded-xl p-5" data-testid={`informativo-${inf.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
                      {inf.publicadoEm ? new Date(inf.publicadoEm).toLocaleDateString("pt-BR") : ""}
                    </div>
                    <div className="font-heading font-bold text-lg">{inf.titulo}</div>
                    <p className="text-sm text-white/70 mt-2 line-clamp-3">{inf.conteudo}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Distribuição da Bolada */}
        {premiacao && (
          <section id="premio" className="surface-card rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <div
              className="absolute top-0 left-0 h-1 w-full"
              style={{ background: "linear-gradient(90deg, #b8860b, #ffd700, #fff3a0, #ffd700, #b8860b)" }}
            />
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil mb-2">Distribuição da Bolada</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase">
              Total em jogo: <span className="text-amarelobrasil">{formatarBRL(premiacao.totalArrecadado)}</span>
            </h2>
            <p className="text-white/60 mt-2 max-w-2xl">
              Distribuição do prêmio entre os participantes:
            </p>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
              {premiacao.distribuicao.map((d, idx) => (
                <CartaoPremio key={d.posicao} posicao={d.posicao} percentual={d.percentual} valor={d.valor} destaque={idx === 0} testId={`premio-${idx + 1}`} />
              ))}
            </div>
          </section>
        )}

        {/* Regulamento */}
        <section id="regras" className="surface-card rounded-2xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-5 h-5 text-amarelobrasil" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Como Funciona</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase">Regulamento Resumido</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6 text-sm text-white/80">
            <Regra titulo="20 pts" desc="Placar exato. Acertou de forma cravada." />
            <Regra titulo="15 pts" desc="Empate ou vencedor com diferença de gols exata." />
            <Regra titulo="10 pts" desc="Acertou só quem vence." />
            <Regra titulo="0 pt" desc="Errou tudo." />
          </div>
          <Link
            to="/regulamento"
            className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amarelobrasil hover:text-white"
            data-testid="link-regras-completas"
          >
            Ler regulamento completo <ChevronRight className="w-4 h-4" />
          </Link>
        </section>
      </div>

      <footer className="border-t border-white/5 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-xs text-white/40 flex flex-wrap items-center justify-between gap-2">
          <div>© 2026 Bolão da Copa do Mundo FIFA 2026 — Desenvolvido por: <a href="https://www.algti.com" target="_blank" rel="noopener noreferrer" className="text-amarelobrasil hover:text-white transition-colors">ALG Soluções em Tecnologia</a></div>
          <Link to="/login" className="hover:text-white">Acesso</Link>
        </div>
      </footer>
    </div>
  );
}

function TacaPremio({ total }) {
  return (
    <div className="relative flex items-center justify-center py-4" data-testid="taca-premio">
      {/* Halo dourado suave (sem bordas marcadas) */}
      <div
        className="absolute inset-0 blur-3xl opacity-35 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 55% at 50% 38%, rgba(255,215,0,0.45), rgba(255,193,7,0.15) 45%, transparent 75%)",
        }}
      />

      <div className="relative w-full max-w-md flex flex-col items-center">
        {/* Taça oficial — fundo preto do PNG some via blend-mode */}
        <div className="relative">
          {/* Spotlight bem suave atrás */}
          <div
            className="absolute -inset-4 blur-3xl opacity-40 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(255,215,0,0.35), transparent 70%)",
            }}
          />
          <img
            src="https://customer-assets.emergentagent.com/job_palpites-mundo/artifacts/lm7h8g1u_Microsoft_Word_-_SolicitaA_C2_A7A_C2_A3o_de_Cancelamento_-_Modelo_assinado.pdf.png"
            alt="Troféu FIFA World Cup 2026"
            className="relative w-full max-w-[340px] mx-auto"
            style={{
              mixBlendMode: "screen",
              filter: "drop-shadow(0 20px 40px rgba(255,215,0,0.25))",
            }}
            data-testid="img-taca-oficial"
          />
        </div>

        {/* Ornamento decorativo entre taça e valor */}
        <div className="flex items-center gap-3 mt-6 mb-4" data-testid="ornamento-divisor">
          <div className="h-px w-12 md:w-20" style={{ background: "linear-gradient(90deg, transparent, #b8860b, #ffd700)" }} />
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <defs>
              <linearGradient id="diamondGold" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fff3a0" />
                <stop offset="50%" stopColor="#ffd700" />
                <stop offset="100%" stopColor="#8a6810" />
              </linearGradient>
            </defs>
            <path d="M7 0 L14 7 L7 14 L0 7 Z" fill="url(#diamondGold)" />
          </svg>
          <div className="font-heading uppercase tracking-[0.45em] text-[10px] md:text-xs font-bold" style={{ color: "#ffd700" }}>
            Prêmio Total em Disputa
          </div>
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M7 0 L14 7 L7 14 L0 7 Z" fill="url(#diamondGold)" />
          </svg>
          <div className="h-px w-12 md:w-20" style={{ background: "linear-gradient(270deg, transparent, #b8860b, #ffd700)" }} />
        </div>

        {/* Valor do prêmio */}
        <div
          className="font-heading font-black tabular-nums leading-none text-5xl md:text-7xl text-center"
          style={{
            background:
              "linear-gradient(180deg, #fff8c8 0%, #ffd700 35%, #b8860b 80%, #5a4209 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            filter: "drop-shadow(0 6px 14px rgba(255,215,0,0.45))",
          }}
          data-testid="valor-premio-total"
        >
          {formatarBRL(total)}
        </div>

        {/* Subtítulo + linha de base */}
        <div className="mt-3 flex items-center gap-3 w-full max-w-xs">
          <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(255,215,0,0.6))" }} />
          <div className="font-heading uppercase tracking-[0.35em] text-[10px] font-bold text-amber-200/70 whitespace-nowrap">
            FIFA World Cup 2026
          </div>
          <div className="flex-1 h-px" style={{ background: "linear-gradient(270deg, transparent, rgba(255,215,0,0.6))" }} />
        </div>
      </div>
    </div>
  );
}

function SVGTroféu() {
  return null;
}

function CartaoPremio({ posicao, percentual, valor, destaque, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl p-5 text-center relative overflow-hidden transition-transform hover:-translate-y-1 ${
        destaque ? "" : "surface-elev"
      }`}
      style={
        destaque
          ? {
              background:
                "linear-gradient(145deg, #3d3015 0%, #5a4209 50%, #3d3015 100%)",
              boxShadow:
                "0 0 0 1px rgba(255,215,0,0.5), 0 12px 30px -6px rgba(255,215,0,0.35)",
            }
          : {}
      }
    >
      <div
        className={`font-heading font-black text-3xl mb-1 ${destaque ? "" : "text-amarelobrasil"}`}
        style={
          destaque
            ? {
                background: "linear-gradient(180deg, #fff3a0, #ffd700 50%, #b8860b)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }
            : {}
        }
      >
        {posicao}
      </div>
      <div className="font-heading font-black text-xl md:text-2xl tabular-nums">
        {formatarBRL(valor)}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">
        {Number(percentual).toFixed(0)}% do total
      </div>
    </div>
  );
}

function MiniJogo({ jogo, futuro }) {
  const m = jogo.selecaoMandante || {};
  const v = jogo.selecaoVisitante || {};
  return (
    <div className="surface-card rounded-xl p-4 flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {getBandeiraUrl(m.sigla) ? (
          <img src={getBandeiraUrl(m.sigla)} alt={m.sigla} className="w-6 h-auto rounded" />
        ) : (
          <span className="text-xs font-bold">{m.sigla}</span>
        )}
        <span className="text-sm font-bold uppercase truncate">{m.sigla}</span>
      </div>
      <div className="px-4">
        {!futuro && jogo.golsMandante != null ? (
          <span className="font-heading font-black text-xl text-amarelobrasil tabular-nums">
            {jogo.golsMandante}<span className="text-white/30 mx-1">×</span>{jogo.golsVisitante}
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-white/40">
            {new Date(jogo.dataHoraJogo).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 min-w-0 flex-1 justify-end">
        <span className="text-sm font-bold uppercase truncate">{v.sigla}</span>
        {getBandeiraUrl(v.sigla) ? (
          <img src={getBandeiraUrl(v.sigla)} alt={v.sigla} className="w-6 h-auto rounded" />
        ) : (
          <span className="text-xs font-bold">{v.sigla}</span>
        )}
      </div>
    </div>
  );
}

function Podio({ ranking }) {
  if (ranking.length === 0) return null;
  const podio = [ranking[1], ranking[0], ranking[2]].filter(Boolean);
  const alturas = ["h-32", "h-44", "h-24"];
  const lugar = ["2º", "1º", "3º"];
  const cores = ["bg-white/10", "bg-amarelobrasil", "bg-verdebrasil"];
  const textos = ["text-white", "text-superficie-base", "text-white"];
  return (
    <div className="flex items-end justify-center gap-2 md:gap-6 mt-6">
      {podio.map((r, idx) => (
        <div key={r.usuarioId} className="flex-1 max-w-[200px] text-center" data-testid={`podio-${lugar[idx]}`}>
          <div className="mb-2">
            <div className="font-bold truncate">{r.usuario?.nomePublico || r.usuario?.nomeCompleto}</div>
            <div className="font-heading font-black text-2xl text-amarelobrasil tabular-nums">{r.pontuacaoTotal}</div>
          </div>
          <div className={`${alturas[idx]} ${cores[idx]} rounded-t-xl flex items-center justify-center ${textos[idx]}`}>
            <span className="font-heading font-black text-3xl md:text-4xl">{lugar[idx]}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function Regra({ titulo, desc }) {
  return (
    <div className="border-l-2 border-verdebrasil pl-4">
      <div className="font-heading font-black text-2xl text-amarelobrasil">{titulo}</div>
      <div className="text-sm text-white/70 mt-1">{desc}</div>
    </div>
  );
}

function LinkAncora({ href, children }) {
  return (
    <a
      href={href}
      className="relative inline-flex items-center justify-center rounded-full p-[1.5px] transition-transform hover:-translate-y-0.5"
      style={{
        background: "linear-gradient(90deg, #009739 0%, #FFDF00 100%)",
        boxShadow: "0 0 0 0 transparent",
      }}
      data-testid={`nav-${href.replace("#", "")}`}
    >
      <span
        className="block px-5 py-2 rounded-full text-xs font-heading font-bold uppercase tracking-widest text-white whitespace-nowrap transition-colors"
        style={{ backgroundColor: "rgba(13,17,23,0.92)" }}
      >
        {children}
      </span>
    </a>
  );
}
